import { useApi } from '@src/app/context/Api';
import { useRoutesContext } from '@src/app/context/Routes/Routes';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { motion } from 'framer-motion';
import React, { useState } from 'react';

import { Details } from './components/Details';
import { Filters } from './components/Filters';
import { ReportLog } from './components/ReportLog';
import { Skeleton } from './components/Skeleton';

/**
 * Page: SourceReportsDashboard
 *
 * Provides a screen to export a report to sharepoint and review a history or reports.
 *
 * @returns {Element}
 * @constructor
 */
export function SourceReportsDashboard() {
  const [sourceTarget, setSourceTarget] = useState(null);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const { currentRoute } = useRoutesContext();
  const { clearApiCache, useApiMutation, useApiQuery } = useApi('Source');

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'Source',
      queryKey: 'ListEimpSources',
    },
  ]);

  const ListSourceReportHistory = useApiQuery('ListSourceReportHistory');

  const [
    GetEimpSource,
    { data: sourceDetail, isFetched: isDetailFetched, isFetching: isDetailFetching, isLoading: isDetailLoading },
  ] = useApiQuery('GetEimpSource');

  const [ScheduleSourceReportExport] = useApiMutation('ScheduleSourceReportExport');

  const form = useForm({
    defaultValues: {
      attribute: '',
      source: '',
    },
  });

  const handleSubmit = (submittedValues) => {
    GetEimpSource(submittedValues);
    setSourceTarget(submittedValues);
    setIsSuccessful(false);
  };

  const handleExport = async () => {
    await ScheduleSourceReportExport(sourceTarget, {
      onError: (error) => {
        setErrorMessage(error);
      },
      onSuccess: async () => {
        setIsSuccessful(true);
        form.reset();
        await clearApiCache([{ apiKey: 'Source', queryKeys: ['ListSourceReportHistory'] }]);
        ListSourceReportHistory?.[1]?.refetch();
      },
    });
  };

  return (
    <ErrorHandler location="src/routes/private/Dashboards/SourceReports/SourceReports.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <FormProvider onSubmit={handleSubmit} state={form}>
          <Grid>
            <Grid.Col span={{ xs: 12 }}>
              <Heading offset={0}>{currentRoute?.screenTitle}</Heading>
            </Grid.Col>
            {isLoadingAssets ? (
              <Grid.Col span={{ xs: 12 }}>
                <Skeleton />
              </Grid.Col>
            ) : (
              <React.Fragment>
                <Grid.Col span={{ xs: 12 }}>
                  <Filters assets={assets} form={form} isSubmitting={isDetailFetching || isDetailLoading} />
                </Grid.Col>
                {isSuccessful && (
                  <Grid.Col span={{ xs: 4 }}>
                    <Alert title="Source Report Sent" variant="success">
                      Refresh Page to check status in the report log table.
                    </Alert>
                  </Grid.Col>
                )}
                {errorMessage && (
                  <Grid.Col span={{ xs: 4 }}>
                    <Alert title="Something went wrong" variant="error">
                      {errorMessage}
                    </Alert>
                  </Grid.Col>
                )}

                <Grid.Col span={{ xs: 4 }}>
                  <Details
                    handleExport={handleExport}
                    isLoading={isDetailFetching || isDetailLoading}
                    show={isDetailFetched && !isSuccessful}
                    sourceDetail={sourceDetail}
                  />
                </Grid.Col>

                <Grid.Col css={{ marginTop: '24px' }} span={{ xs: 12 }}>
                  <ReportLog assets={assets} query={ListSourceReportHistory} />
                </Grid.Col>
              </React.Fragment>
            )}
          </Grid>
        </FormProvider>
      </motion.div>
    </ErrorHandler>
  );
}
